import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import usePageTitle from '../hooks/usePageTitle';
import api from '../services/api';
import './AdminGiveaways.css';

const AdminGiveaways = () => {
  usePageTitle('Manage Giveaways');
  const navigate = useNavigate();
  const { isFounder } = useAuth();
  const { success, error: showError } = useToast();
  const [giveaways, setGiveaways] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingGiveaway, setEditingGiveaway] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    required_points: '',
    quantity: '',
    is_active: true,
    image: null,
  });
  const [imagePreview, setImagePreview] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isFounder) {
      navigate('/admin');
      return;
    }
    fetchGiveaways();
  }, [isFounder, navigate]);

  const fetchGiveaways = async () => {
    setLoading(true);
    try {
      const response = await api.get('/giveaways');
      const giveawaysData = Array.isArray(response.data) 
        ? response.data 
        : (response.data?.data && Array.isArray(response.data.data) 
          ? response.data.data 
          : []);
      setGiveaways(giveawaysData);
    } catch (error) {
      console.error('Error fetching giveaways:', error);
      showError('Error fetching giveaways');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (giveaway = null) => {
    if (giveaway) {
      setEditingGiveaway(giveaway);
      setFormData({
        title: giveaway.title || '',
        description: giveaway.description || '',
        required_points: giveaway.required_points || '',
        quantity: giveaway.quantity !== null && giveaway.quantity !== undefined ? giveaway.quantity : '',
        is_active: giveaway.is_active !== undefined ? giveaway.is_active : true,
        image: null,
      });
      setImagePreview(giveaway.image_url || giveaway.image ? (giveaway.image_url ? `${giveaway.image_url}?t=${Date.now()}` : `https://aliensteam.com/storage/${giveaway.image}?t=${Date.now()}`) : null);
    } else {
      setEditingGiveaway(null);
      setFormData({
        title: '',
        description: '',
        required_points: '',
        quantity: '',
        is_active: true,
        image: null,
      });
      setImagePreview(null);
    }
    setShowModal(true);
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData({ ...formData, image: file });
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const submitData = new FormData();
      submitData.append('title', formData.title);
      submitData.append('description', formData.description);
      submitData.append('required_points', formData.required_points);
      submitData.append('quantity', formData.quantity || '0');
      submitData.append('is_active', formData.is_active ? '1' : '0');
      if (formData.image) {
        submitData.append('image', formData.image);
      }

      if (editingGiveaway) {
        // Use POST with method spoofing for better FormData compatibility
        submitData.append('_method', 'PUT');
        await api.post(`/giveaways/${editingGiveaway.id}`, submitData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        success('Giveaway updated successfully!');
      } else {
        await api.post('/giveaways', submitData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        success('Giveaway created successfully!');
      }

      setShowModal(false);
      fetchGiveaways();
    } catch (error) {
      console.error('Error saving giveaway:', error);
      showError(error.response?.data?.message || 'Error saving giveaway');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (giveawayId) => {
    if (!window.confirm('Are you sure you want to delete this giveaway?')) {
      return;
    }

    try {
      await api.delete(`/giveaways/${giveawayId}`);
      success('Giveaway deleted successfully!');
      fetchGiveaways();
    } catch (error) {
      console.error('Error deleting giveaway:', error);
      showError(error.response?.data?.message || 'Error deleting giveaway');
    }
  };

  const toggleActive = async (giveaway) => {
    try {
      const submitData = new FormData();
      submitData.append('title', giveaway.title);
      submitData.append('description', giveaway.description || '');
      submitData.append('required_points', giveaway.required_points);
      submitData.append('quantity', giveaway.quantity !== null && giveaway.quantity !== undefined ? giveaway.quantity : '0');
      submitData.append('is_active', (!giveaway.is_active).toString());

      await api.put(`/giveaways/${giveaway.id}`, submitData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      success(`Giveaway ${!giveaway.is_active ? 'activated' : 'deactivated'} successfully!`);
      fetchGiveaways();
    } catch (error) {
      console.error('Error toggling active status:', error);
      showError('Error updating giveaway status');
    }
  };

  if (loading) {
    return (
      <div className="admin-giveaways-container">
        <div className="loading-spinner"></div>
        <p className="loading-text">Loading giveaways...</p>
      </div>
    );
  }

  return (
    <div className="admin-giveaways-container">
      <div className="admin-giveaways-header">
        <button className="btn-back" onClick={() => navigate('/admin')}>
          ← Back to Dashboard
        </button>
        <h1>🎁 Manage Giveaways</h1>
        <button className="btn-primary" onClick={() => handleOpenModal()}>
          ➕ Add New Giveaway
        </button>
      </div>

      <div className="giveaways-table-container">
        {giveaways.length === 0 ? (
          <div className="no-giveaways">
            <p>No giveaways found. Create your first giveaway!</p>
          </div>
        ) : (
          <table className="giveaways-table">
            <thead>
              <tr>
                <th>Image</th>
                <th>Title</th>
                <th>Required Points</th>
                <th>Quantity</th>
                <th>Claimed</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {giveaways.map((giveaway) => (
                <tr key={giveaway.id}>
                  <td>
                    <div className="giveaway-image-cell">
                      {giveaway.image_url || giveaway.image ? (
                        <img
                          src={giveaway.image_url ? `${giveaway.image_url}?t=${Date.now()}` : `https://aliensteam.com/storage/${giveaway.image}?t=${Date.now()}`}
                          alt={giveaway.title}
                          className="giveaway-thumbnail"
                          onError={(e) => {
                            e.target.src = 'https://via.placeholder.com/50x50/0a0e27/00ff88?text=Giveaway';
                          }}
                        />
                      ) : (
                        <div className="giveaway-placeholder-small">🎁</div>
                      )}
                    </div>
                  </td>
                  <td>
                    <div className="giveaway-title-cell">
                      <strong>{giveaway.title}</strong>
                      {giveaway.description && (
                        <span className="giveaway-description-small">
                          {giveaway.description.substring(0, 50)}
                          {giveaway.description.length > 50 ? '...' : ''}
                        </span>
                      )}
                    </div>
                  </td>
                  <td>
                    <span className="points-badge">⭐ {giveaway.required_points || 0}</span>
                  </td>
                  <td>
                    {giveaway.quantity > 0 
                      ? giveaway.quantity 
                      : '∞'}
                  </td>
                  <td>
                    {giveaway.claimed_count || 0}
                  </td>
                  <td>
                    <button
                      className={`status-toggle ${giveaway.is_active ? 'active' : 'inactive'}`}
                      onClick={() => toggleActive(giveaway)}
                    >
                      {giveaway.is_active ? '✅ Active' : '❌ Inactive'}
                    </button>
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button
                        className="btn-edit"
                        onClick={() => handleOpenModal(giveaway)}
                      >
                        ✏️ Edit
                      </button>
                      <button
                        className="btn-delete"
                        onClick={() => handleDelete(giveaway.id)}
                      >
                        🗑️ Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Giveaway Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content giveaway-modal" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowModal(false)}>×</button>
            <h2>{editingGiveaway ? 'Edit Giveaway' : 'Add New Giveaway'}</h2>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Title *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows="3"
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Required Points *</label>
                  <input
                    type="number"
                    min="1"
                    value={formData.required_points}
                    onChange={(e) => setFormData({ ...formData, required_points: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Quantity (0 for unlimited)</label>
                  <input
                    type="number"
                    min="0"
                    value={formData.quantity}
                    onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                  />
                </div>
              </div>
              <div className="form-group">
                <label>
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  />
                  Active (visible to users)
                </label>
              </div>
              <div className="form-group">
                <label>Giveaway Image</label>
                {imagePreview && (
                  <div className="image-preview">
                    <img src={imagePreview} alt="Preview" />
                  </div>
                )}
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                />
              </div>
              <div className="modal-actions">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => setShowModal(false)}
                  disabled={saving}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={saving}
                >
                  {saving ? 'Saving...' : editingGiveaway ? 'Update Giveaway' : 'Create Giveaway'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminGiveaways;

